/**
 * 팝업 API 클라이언트.
 *
 * 영구지침: docs/develop/popup/popup-spec.md §6
 */
import apiClient from '@/lib/api/client'
import type {
  AdminPopupResponse,
  PopupCreateRequest,
  PopupResponse,
  PopupStatus,
} from './types'

interface ApiEnvelope<T> {
  success: boolean
  data: T
}

interface PageResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

// ─────────────────────────────────────────────────────────────────────────────
// 사용자용
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchActivePopups(path: string): Promise<PopupResponse[]> {
  const { data } = await apiClient.get<ApiEnvelope<PopupResponse[]>>(
    `/popups?path=${encodeURIComponent(path)}`
  )
  return data.data
}

export async function recordImpression(popupId: number): Promise<void> {
  try {
    await apiClient.post(`/popups/${popupId}/impression`)
  } catch {
    /* silent fail — UX 우선 */
  }
}

export async function recordClick(popupId: number): Promise<void> {
  try {
    await apiClient.post(`/popups/${popupId}/click`)
  } catch {
    /* silent fail */
  }
}

export async function recordDismiss(popupId: number): Promise<void> {
  try {
    await apiClient.post(`/popups/${popupId}/dismiss`)
  } catch {
    /* silent fail */
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 어드민용
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchAdminPopups(
  page = 0,
  size = 20
): Promise<PageResponse<AdminPopupResponse>> {
  const { data } = await apiClient.get<ApiEnvelope<PageResponse<AdminPopupResponse>>>(
    `/admin/popups?page=${page}&size=${size}`
  )
  return data.data
}

export async function fetchAdminPopupById(id: number): Promise<AdminPopupResponse> {
  const { data } = await apiClient.get<ApiEnvelope<AdminPopupResponse>>(
    `/admin/popups/${id}`
  )
  return data.data
}

export async function createPopup(request: PopupCreateRequest): Promise<AdminPopupResponse> {
  const { data } = await apiClient.post<ApiEnvelope<AdminPopupResponse>>(
    '/admin/popups',
    request
  )
  return data.data
}

export async function updatePopup(
  id: number,
  request: PopupCreateRequest
): Promise<AdminPopupResponse> {
  const { data } = await apiClient.put<ApiEnvelope<AdminPopupResponse>>(
    `/admin/popups/${id}`,
    request
  )
  return data.data
}

export async function changePopupStatus(id: number, status: PopupStatus): Promise<void> {
  await apiClient.patch(`/admin/popups/${id}/status?status=${status}`)
}

export async function deletePopup(id: number): Promise<void> {
  await apiClient.delete(`/admin/popups/${id}`)
}
